/**
 * BCE URL Params Module
 *
 * Reads URL parameters and pre-fills form fields (Powermail forms)
 * Supports input fields, select fields, textareas, and Nice Select plugin
 *
 * Usage: Works automatically on all Powermail forms!
 * Optional: Add class "bce_url-params" for explicit control
 *
 * @class BceUrlParams
 */
class BceUrlParams {
	constructor() {
		this.instances = new WeakMap();
		this.autoInit = true; // Enable auto-init for global usage
		this.globalInitialized = false;
	}

	/**
	 * Initialize all url-params containers
	 * @param {string} selector - CSS selector for containers
	 */
	init(selector = '.bce_url-params') {
		// First: Initialize explicit containers with bce_url-params class
		const containers = document.querySelectorAll(selector);
		containers.forEach((el) => {
			if (!this.instances.has(el)) {
				this.create(el);
			}
		});

		// Second: Auto-detect and initialize ALL Powermail forms globally
		if (!this.globalInitialized) {
			this.initGlobalPowermailForms();
			this.globalInitialized = true;
		}
	}

	/**
	 * Initialize all Powermail forms automatically
	 * Finds forms by Powermail field name pattern
	 */
	initGlobalPowermailForms() {
		// Find all forms containing Powermail fields
		const powermailFields = document.querySelectorAll('[name*="tx_powermail_pi1[field]"]');
		const forms = new Set();

		// Get unique parent forms
		powermailFields.forEach((field) => {
			const form = field.closest('form');
			if (form) {
				forms.add(form);
			}
		});

		// Initialize each form
		forms.forEach((form) => {
			if (!this.instances.has(form)) {
				this.create(form);
			}
		});
	}

	/**
	 * Create a url-params instance for a container
	 * @param {HTMLElement} element - Container element (usually a form or form wrapper)
	 * @returns {Object|null} Instance object or null
	 */
	create(element) {
		if (this.instances.has(element)) {
			return this.instances.get(element);
		}

		// Parse URL parameters
		const urlParams = new URLSearchParams(window.location.search);

		// Track filled fields for instance
		const filledFields = [];

		// Process each URL parameter
		urlParams.forEach((value, key) => {
			// Try to fill select field
			const selectField = this.fillSelectField(element, key, value);
			if (selectField) {
				filledFields.push({ type: 'select', element: selectField, key, value });
			}

			// Try to fill input field
			const inputField = this.fillInputField(element, key, value);
			if (inputField) {
				filledFields.push({ type: 'input', element: inputField, key, value });
			}
		});

		// Create instance
		const instance = {
			element,
			filledFields,
			urlParams: Object.fromEntries(urlParams),
			destroy: () => {
				// Clear filled fields
				filledFields.forEach(({ element }) => {
					if (element.tagName === 'SELECT') {
						element.selectedIndex = 0;
					} else {
						element.value = '';
					}
				});
				this.instances.delete(element);
			}
		};

		this.instances.set(element, instance);
		return instance;
	}

	/**
	 * Fill a select field with URL parameter value
	 * @param {HTMLElement} container - Container element to search within
	 * @param {string} key - URL parameter key (field name)
	 * @param {string} value - URL parameter value
	 * @returns {HTMLElement|null} Select element or null
	 */
	fillSelectField(container, key, value) {
		// Powermail field name pattern: tx_powermail_pi1[field][fieldname]
		const selectField = container.querySelector(`select[name="tx_powermail_pi1\\[field\\]\\[${key}\\]"]`);

		if (!selectField) {
			return null;
		}

		// Set value
		selectField.value = value;

		// Dispatch change event to trigger any listeners
		selectField.dispatchEvent(new Event('change', { bubbles: true }));

		// Update Nice Select plugin if present
		this.updateNiceSelect(selectField, value);

		return selectField;
	}

	/**
	 * Fill an input field with URL parameter value
	 * @param {HTMLElement} container - Container element to search within
	 * @param {string} key - URL parameter key (field name)
	 * @param {string} value - URL parameter value
	 * @returns {HTMLElement|null} Input element or null
	 */
	fillInputField(container, key, value) {
		// Powermail field name pattern: tx_powermail_pi1[field][fieldname]
		// Try input field first
		let inputField = container.querySelector(`input[name="tx_powermail_pi1\\[field\\]\\[${key}\\]"]`);

		// Try textarea if input not found
		if (!inputField) {
			inputField = container.querySelector(`textarea[name="tx_powermail_pi1\\[field\\]\\[${key}\\]"]`);
		}

		if (!inputField) {
			return null;
		}

		// Set value
		inputField.value = value;

		// Dispatch input event to trigger any listeners
		inputField.dispatchEvent(new Event('input', { bubbles: true }));

		return inputField;
	}

	/**
	 * Update Nice Select plugin dropdown
	 * @param {HTMLElement} selectField - Native select element
	 * @param {string} value - Selected value
	 */
	updateNiceSelect(selectField, value) {
		// Check if Nice Select is initialized (next sibling is .nice-select)
		const dropdown = selectField.nextElementSibling;

		if (!dropdown || !dropdown.classList.contains('nice-select')) {
			return;
		}

		// Find option with matching value
		const optionElement = dropdown.querySelector(`.option[data-value="${value}"]`);

		if (optionElement) {
			// Simulate click to update Nice Select UI
			optionElement.click();

			// Close dropdown
			dropdown.classList.remove('open');
		}
	}

	/**
	 * Get instance for external access
	 * @param {HTMLElement} element - Container element
	 * @returns {Object|null} Instance object or null
	 */
	getInstance(element) {
		return this.instances.get(element) || null;
	}

	/**
	 * Destroy instance and clean up
	 * @param {HTMLElement} element - Container element
	 */
	destroy(element) {
		const instance = this.instances.get(element);
		if (instance && instance.destroy) {
			instance.destroy();
		}
	}

	/**
	 * Check if URL has parameters that could be used for form filling
	 * @returns {boolean} True if URL has parameters
	 */
	hasUrlParams() {
		return window.location.search.length > 1;
	}
}

// Create singleton instance
const urlParamsInstance = new BceUrlParams();

// Auto-initialize when DOM is ready if URL params exist
if (typeof window !== 'undefined') {
	const autoInit = () => {
		if (urlParamsInstance.hasUrlParams()) {
			console.log('BCE URL Params: Auto-initializing (URL params detected)');
			urlParamsInstance.init();
		}
	};

	// Run on DOM ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', autoInit);
	} else {
		// DOM already loaded
		autoInit();
	}
}

export default urlParamsInstance;
